import type {
  RemoteParticipant,
  RemoteTrackPublication,
  Room,
  Track,
} from 'livekit-client'
import {
  type DeadAirEpisode,
  type DeadAirFork,
  type DeadAirSink,
  type DeadPeer,
} from '../../util/deadAirTypes'
import {
  unpackAudioReceiverStats,
  type AudioReceiverStats,
  type StatsReadableTrack,
} from '../../util/audioReceiverStats'
import { ServerIdentitiesIds } from '../../util/identities'
import { isWebKit } from '../../util/webkit'

/**
 * DeadAirDetector — the inbound (reception-side) dead-air detector.
 *
 * It walks the **publishing roster** (remote participants with an audio
 * publication) and runs an ordered set of checks on each peer. The **wiring
 * checks** read synchronous JS properties only — no `getStats`, no `await` — so
 * they are cheap enough to run on slide-change, on `trackSubscribed`, once per
 * sustained speaking turn, and on a slow backstop sweep. The **flow checks**
 * (getStats, speaker-gated) are a separate step; they never run inside this
 * walk, though the same speaking turn triggers both.
 *
 * The class is a long-lived, non-React object: the failure moment is a
 * slide-transition re-render that unmounts components, so no diagnostic state
 * can be React state. It attaches to a LiveKit `Room` (its own `trackSubscribed`
 * listener + backstop timer) and is fed slide changes by the controller.
 *
 * Everything the detector needs from the outside world arrives through
 * {@link DeadAirEnvironment}, so the logic is unit-testable against fakes with
 * no real LiveKit room. {@link createLivekitDeadAirEnvironment} is the adapter
 * that maps a real `Room` onto that interface.
 */

/** A media element as the wiring checks read it (sync properties only). */
export interface DetectorMediaElement {
  paused: boolean
  currentTime: number
  /** '' / 'default' both mean the default output device. */
  sinkId?: string
}

/** A subscribed remote audio track as the wiring checks read it. */
export interface DetectorAudioTrack {
  sid?: string
  attachedElements: DetectorMediaElement[]
}

/** One audio publication on a publishing peer. */
export interface DetectorPublication {
  trackSid: string
  isSubscribed: boolean
  isMuted: boolean
  /**
   * The LiveKit track source ('microphone', 'screen_share_audio', …). Carried
   * so the wiring checks can name the mic — see {@link micOrAllPublications}.
   */
  source?: string
  audioTrack?: DetectorAudioTrack
}

/** A publishing peer (a remote participant with ≥1 audio publication). */
export interface DetectorPeer {
  identity: string
  audioPublications: DetectorPublication[]
}

/** Everything the detector reads from outside; injectable for tests. */
export interface DeadAirEnvironment {
  /** This client's user id (the listener). */
  listenerId(): string
  /** The publishing roster: remote peers with an audio publication. */
  roster(): DetectorPeer[]
  /**
   * How many remote peers this client is subscribed to (≥1 audio track).
   * Counts the same population as {@link roster} — human peers only — so the
   * two numbers an episode reports are comparable.
   */
  peersSubscribed(): number
  /** Active audio-output device id ('' / 'default' == default). */
  activeAudioOutput(): string | undefined
  /** document.hidden at scan time. */
  pageHidden(): boolean
  /** LiveKit room connection state (e.g. connected/reconnecting). */
  connectionState(): string
  /**
   * The subscriber PeerConnection's transport state, or `{}` when neither read
   * reached one.
   *
   * Its own provider rather than something derived from
   * {@link DeadAirEnvironment.connectionState}, because the two disagree in the
   * one case that matters. LiveKit sets the room back to `connected` only after
   * its own `waitForPCReconnected()` resolves, so a resume that reported
   * success over a transport that was still dead — or that re-broke straight
   * afterwards — leaves the room reading `connected` across a subscriber PC
   * reading `disconnected`. Nothing else in this interface can see that, and
   * while it lasts no inbound RTP arrives for anybody: every attached element's
   * playhead freezes and the wiring walk condemns the whole roster `lost_play`.
   *
   * Both fields are optional on purpose. See `isTransportDown` for why an
   * absent read has to mean "healthy" rather than "down".
   */
  subscriberTransport(): { ice?: string; pc?: string }
  /**
   * Whether a `trackSubscriptionFailed` already fired for this track sid. The
   * `not_subscribed` failure type is the *silent* case only — an already-
   * reported subscription error is a different, existing signal and must not be
   * double-counted.
   */
  hadSubscriptionFailure(trackSid: string): boolean
  /**
   * Read the inbound-RTP stats for one named speaker (flow checks only).
   * Scoped to that single peer — never a per-event roster sweep.
   */
  getReceiverStats(identity: string): Promise<AudioReceiverStats | undefined>
  /** Whether the named speaker's audio publication is currently muted. */
  isSpeakerAudioMuted(identity: string): boolean
  /**
   * Whether this listener is sequestered (isolated), so silence is expected.
   *
   * Suppresses every wiring fork, not one. How sequestration silences a peer
   * is the controller's business (`setEnabled(false)` today, which keeps the
   * track subscribed and attached with a frozen playhead; `setSubscribed(false)`
   * before that), and the detector must not report whichever fault that
   * mechanism happens to resemble.
   */
  isListenerSequestered(): boolean
  /**
   * Whether a repair this client started is running right now.
   *
   * The dialog's reconnect tears down every subscription in the room on
   * purpose, so the churn it produces is expected silence rather than dead air
   * — the same shape as {@link DeadAirEnvironment.isListenerSequestered},
   * though narrower: only the `not_subscribed` fork is suppressed for a
   * repair, where sequestration suppresses the whole wiring walk.
   *
   * It needs its own provider because {@link DeadAirEnvironment.connectionState}
   * cannot see it. `MeetingCubit.reconnectRoom` does an explicit
   * `disconnect()` / `connect()` pair, so `room.state` reads
   * disconnected -> connecting -> connected and never once reports
   * `reconnecting`, which is the state every other guard here watches for.
   */
  isRepairInFlight(): boolean
  /**
   * Whether this listener has finished joining the meeting room.
   *
   * The detector attaches when the LiveKit `Room` object is constructed, and
   * that happens before the AV check rather than after it, so peers who are
   * already publishing are visible for however long the user spends choosing a
   * camera. That time is user-controlled and unbounded, so no
   * {@link DeadAirDetectorOptions.attachGraceMs} value covers it: the meeting
   * UI has not mounted, nothing has attached an element, and every peer in the
   * room classifies `not_attached`.
   *
   * Gates emission only. The ring, the playhead memory and the speaker
   * accumulators keep filling through the AV check, so a peer who was already
   * broken when the user walked in still has history behind them.
   */
  hasListenerJoined(): boolean
  /**
   * Whether this client runs on WebKit.
   *
   * A constant rather than a provider: the engine cannot change inside a
   * session, and the checks read it on every publication.
   *
   * Here because `setSinkId` rejects with `NotAllowedError` outside a transient
   * user gesture on WebKit, and remote audio elements are created by React
   * commits for the whole meeting, never by a click. An element that attached
   * outside the join gesture can therefore never adopt a pinned output device,
   * which the sink check would otherwise read as this peer's wiring being
   * broken. See {@link DeadAirDetector.classifyPublication}.
   */
  isWebKit: boolean
  /**
   * How LiveKit rates this client's own connection right now
   * (`excellent` / `good` / `poor` / `lost` / `unknown`), or undefined when the
   * read did not reach one.
   *
   * `connectionQualityChanged` already fires on the room, but nothing joins it
   * onto an episode, and this is what separates "this student's network" from
   * "this fault".
   */
  connectionQuality(): string | undefined
  /**
   * How many human participants are in the room, this client included, or
   * undefined when the read did not reach one.
   *
   * `peersPublishing` and `peersSubscribed` both count publishing peers, so
   * neither sees a participant who is simply there. Without this, `peersDead: 2`
   * cannot be read as a whole 2-person room or a third of a 6-person one.
   *
   * Counted over the same population those two are — server identities
   * excluded — or the three numbers on one episode would be drawn from
   * different rosters.
   */
  participantCount(): number | undefined
  /**
   * Build the diagnostic wiring snapshot for one dead peer: its audio
   * element(s), decoded track, transceiver/receiver, the engine
   * PeerConnection state, and the output-device picture. Every field is a
   * guarded read, so a missing or SDK-changed value degrades to `undefined`
   * instead of throwing. Read at emit time; never gated on.
   */
  snapshotPeer(identity: string): Record<string, unknown>
}

export interface DeadAirDetectorOptions {
  env: DeadAirEnvironment
  /** Where a confirmed episode goes; the detector calls `emit` and nothing else. */
  sink: DeadAirSink
  /** Injectable clock (defaults to Date.now). */
  now?: () => number
  /**
   * A non-paused element whose `currentTime` has not advanced for at least
   * this long is treated as a frozen playhead (`lost_play`).
   */
  frozenPlayheadMs?: number
  /** Backstop structural-sweep interval for tracks that silently lose an element. */
  backstopIntervalMs?: number
  /** Cumulative active-speaker time a peer must sustain before a behavioral check. */
  sustainedSpeakerMs?: number
  /**
   * Largest gap between `activeSpeakersChanged` events still counted as one
   * continuous speaking turn. A larger gap ends the turn and resets what it
   * carried.
   */
  speakerFlickerGapMs?: number
  /** How long after subscription a track may have no attached element. */
  attachGraceMs?: number
  /** Energy delta above which the decoder is judged to have produced sound. */
  energyEpsilon?: number
  /** Fraction of concealed samples above which flat energy is masked packet loss. */
  concealFraction?: number
  /**
   * Called on the dead -> clean edge: a structural scan found every publishing
   * peer healthy after a previous scan had reported one dead. The UI uses this
   * to cancel a pending nudge, so it fires on the transition only, not on
   * every clean scan.
   */
  onRecovered?: () => void
}

const DEFAULT_FROZEN_PLAYHEAD_MS = 4000
const DEFAULT_BACKSTOP_INTERVAL_MS = 30_000
// `trackSubscribed` fires before the renderer attaches an <audio> element, so
// every fresh subscription looks unattached for a moment. Suppress
// `not_attached` for this long after subscription; the backstop sweep re-checks
// afterwards, so a track that genuinely never attaches is still reported.
const DEFAULT_ATTACH_GRACE_MS = 2000
const DEFAULT_SUSTAINED_SPEAKER_MS = 2500
const DEFAULT_ENERGY_EPSILON = 1e-6
const DEFAULT_CONCEAL_FRACTION = 0.1
// activeSpeakersChanged flickers several times/sec; only gaps below this are
// counted as continuous speech. A larger gap ends the current speaking turn.
// Overridable from settings/dead_air_detector, because the right value depends
// on the SFU's event cadence and both directions cost something: too low and a
// turn never accumulates the sustained window, so flow faults go unevaluated;
// too high and a speaker who pauses between sentences is judged once per
// meeting against a stale baseline.
const DEFAULT_SPEAKER_FLICKER_GAP_MS = 800
// A speaker unseen for this long resets, so the next turn re-baselines.
const SPEAKER_WINDOW_RESET_MS = 3000
// How long a flow verdict stands without fresh evidence. A condemned peer who
// goes quiet can never be cleared by the only positive proof there is (energy
// from their next speaking turn), so without an expiry one stall latches
// `onRecovered` off for the rest of the session. Must comfortably exceed the
// auto-heal settle window, or a heal verify would run after the verdict it is
// meant to consult has already gone.
const BEHAVIORAL_VERDICT_TTL_MS = 15_000
// Byte delta at or below this over a sustained speaking turn is "no delivery".
const BYTES_STALLED_EPSILON = 0
/** `Track.Source.Microphone`'s runtime value. */
const MICROPHONE_SOURCE_ID = 'microphone'
// livekit-client is imported as `import type` only, so that firebase-repository
// doesn't pull it into the eager bundle. The enum member is the string above at
// runtime, cast here to keep type-safety without a runtime reference — the same
// trade MeetingBackgroundBlurApplier makes for the camera.
const MICROPHONE_SOURCE = MICROPHONE_SOURCE_ID as Track.Source

/** `Track.Kind.Audio`'s runtime value, cast for the same reason as above. */
const AUDIO_KIND_ID = 'audio'

/** `ConnectionState.Connected`'s runtime value; see the cast note above. */
const CONNECTED_STATE = 'connected'

/**
 * The peer's microphone publication, or undefined if they publish audio from
 * some other source only. Peers publish a single mic track today; screen-share
 * audio would add a second, and the checks that mean "is this speaker audible"
 * must name the track the speaker is actually talking into.
 */
function findMicPublication(
  participant: RemoteParticipant
): RemoteTrackPublication | undefined {
  for (const pub of participant.audioTrackPublications.values()) {
    if (pub.source === MICROPHONE_SOURCE) return pub
  }
  return undefined
}

/**
 * The publications the wiring checks judge a peer on: the microphone alone when
 * they publish one, every audio publication when they don't.
 *
 * A dead-air verdict means "this listener cannot hear the speaker", and the
 * speaker talks into the mic. Judging every publication would report dead air
 * for a peer whose mic is subscribed, attached and audible, on the strength of
 * a screen-share audio track nobody is talking over — the same reason
 * {@link findMicPublication} exists for the flow checks. The fallback keeps a
 * peer who publishes audio from some other source under observation rather than
 * exempting them from the walk entirely.
 */
function micOrAllPublications(peer: DetectorPeer): DetectorPublication[] {
  const mic = peer.audioPublications.find(
    (pub) => pub.source === MICROPHONE_SOURCE_ID
  )
  return mic ? [mic] : peer.audioPublications
}

/** '' / 'default' / undefined all denote the default output device. */
export function normalizeSink(sinkId: string | undefined): string {
  if (sinkId === undefined || sinkId === '' || sinkId === 'default') {
    return 'default'
  }
  return sinkId
}

interface Playhead {
  currentTime: number
  /** When `currentTime` was last observed to change. */
  at: number
}

/** A standing flow verdict against one peer, with the time it was recorded. */
interface BehavioralVerdict {
  fork: DeadAirFork
  at: number
}

/** Per-remote-speaker accumulator for the behavioral speaker gate. */
interface SpeakerState {
  /** Continuous active-speaker time accumulated since `baseline` was taken. */
  activeMs: number
  /** Last moment this speaker was seen active. */
  lastSeenAt: number
  /**
   * When this speaking turn began. A second clock alongside `activeMs`, which
   * restarts when the baseline lands and never advances at all when there is
   * no baseline to capture. The structural sweep needs neither, so it is timed
   * from here — see {@link DeadAirDetector.onActiveSpeakers}.
   */
  turnStartedAt: number
  /** Stats snapshot this turn's deltas are measured against. */
  baseline: AudioReceiverStats | null
  /** True once this turn tried to capture a baseline (null = unavailable). */
  baselineAttempted: boolean
  /** True once this turn has been evaluated, so it fires at most once. */
  evaluated: boolean
  /** True once this turn drove a structural sweep, so it fires at most once. */
  structurallyScanned: boolean
}

export class DeadAirDetector {
  private readonly env: DeadAirEnvironment
  private readonly sink: DeadAirSink
  private readonly now: () => number
  private readonly frozenPlayheadMs: number
  private readonly backstopIntervalMs: number
  private readonly sustainedSpeakerMs: number
  private readonly speakerFlickerGapMs: number
  private readonly attachGraceMs: number
  private readonly energyEpsilon: number
  private readonly concealFraction: number
  private readonly onRecovered?: () => void

  private unsubscribers: (() => void)[] = []
  private backstopTimer: ReturnType<typeof setInterval> | null = null
  /** Per-track playhead memory for frozen-currentTime detection. */
  private readonly playheads = new Map<string, Playhead>()
  /** Per-remote-speaker accumulators for the flow checks. */
  private readonly speakers = new Map<string, SpeakerState>()
  /**
   * The last inbound-RTP sample read for each peer, so a structural episode can
   * carry the counters without taking a `getStats` of its own.
   *
   * The wiring walk is synchronous and stays that way — see
   * {@link DeadAirDetector.scanStructural}. This is the only way its episodes
   * can answer "was audio arriving at all", which is the question that
   * separates a real fault from a detector artifact, and until it existed every
   * structural report reached Sentry with an empty `receiverStats`.
   *
   * Written wherever a flow check already paid for a read, so it costs nothing
   * extra. Stale by construction, hence the bound in
   * {@link DeadAirDetector.recentReceiverStats} and the age stamped on every
   * episode that carries one.
   */
  private readonly lastReceiverStats = new Map<
    string,
    { stats: AudioReceiverStats; at: number }
  >()

  /** True once a scan has reported dead peers, until a clean scan clears it. */
  private reportedDead = false
  /**
   * When each peer was first reported dead in the current run, so an episode
   * can say how long the fault has lasted rather than only that it is still
   * there.
   *
   * Written at the single emit exit rather than during the walk, so a peer
   * condemned by a scan the join gate drops does not date the run to a room
   * the user had not entered yet.
   *
   * Cleared by the dead -> clean edge, and for one peer when they leave the
   * roster. See {@link DeadPeer.deadForMs} for why a peer's absence from one
   * scan must not.
   */
  private readonly deadSince = new Map<string, number>()
  /** When the current dead-air run began; null between runs. */
  private deadRunStartedAt: number | null = null
  /**
   * Peers a flow check has condemned, pending a healthy re-check. The wiring
   * walk cannot see a flow fault (a stalled peer is fully subscribed, attached
   * and playing as far as it can tell), so without this the next clean sweep
   * would report recovery for a client still deaf.
   *
   * Keyed to the failure type that condemned them, so a verdict can be lifted by
   * the evidence that actually disproves it: resumed delivery clears
   * `delivery_stalled` without needing the energy fields a `silent_decode`
   * verdict does. Stamped with the time it was recorded so a verdict nothing has
   * disproved still expires — see {@link BEHAVIORAL_VERDICT_TTL_MS}.
   */
  private readonly behaviorallyDead = new Map<string, BehavioralVerdict>()
  /**
   * Set during a live walk when the repair guard sent a fork back as null, and
   * read by the dead -> clean edge below. A walk that suppressed something has
   * no opinion on whether this client can hear: the peers it went quiet about
   * may be exactly the ones still broken.
   *
   * Only the repair guard counts. Sequestration is expected silence that lasts
   * a whole quiz, so letting it set this would latch the detector dead for the
   * duration.
   */
  private repairSuppressedThisScan = false
  /** Per-track deadline before which an unattached element is not yet dead. */
  private readonly attachGraceUntil = new Map<string, number>()
  /** When the last slide transition was seen; stamps episode timing. */
  private lastSlideChangeAt: number | null = null
  /**
   * The joined state as the last scan saw it, or null before the first scan.
   * Null and true are both "no edge to act on" — see
   * {@link armAttachGraceOnJoin}.
   */
  private joinSeen: boolean | null = null
  /**
   * Set by detach() so an in-flight async check can't emit after teardown, and
   * never lifted: a detached detector is spent, not reusable. See {@link attach}.
   */
  private disposed = false

  constructor(options: DeadAirDetectorOptions) {
    this.env = options.env
    this.sink = options.sink
    this.now = options.now ?? Date.now
    this.frozenPlayheadMs =
      options.frozenPlayheadMs ?? DEFAULT_FROZEN_PLAYHEAD_MS
    this.backstopIntervalMs =
      options.backstopIntervalMs ?? DEFAULT_BACKSTOP_INTERVAL_MS
    this.sustainedSpeakerMs =
      options.sustainedSpeakerMs ?? DEFAULT_SUSTAINED_SPEAKER_MS
    this.speakerFlickerGapMs =
      options.speakerFlickerGapMs ?? DEFAULT_SPEAKER_FLICKER_GAP_MS
    this.attachGraceMs = options.attachGraceMs ?? DEFAULT_ATTACH_GRACE_MS
    this.energyEpsilon = options.energyEpsilon ?? DEFAULT_ENERGY_EPSILON
    this.concealFraction = options.concealFraction ?? DEFAULT_CONCEAL_FRACTION
    this.onRecovered = options.onRecovered
  }

  /**
   * Register the detector's own room listeners + backstop sweep.
   *
   * A detector serves exactly one room: its {@link DeadAirEnvironment} is bound
   * to that room at construction, so neither repeating an attach nor reviving a
   * detached detector can mean anything useful. Both are no-ops rather than
   * errors, because this is a diagnostic path and must never be the reason a
   * meeting fails to set up.
   *
   * Without the first guard a second attach started a second interval and
   * overwrote the handle to the first, so every sweep ran twice and `detach`
   * cleared only the newer timer — the older one swept for the life of the page.
   */
  attach(room: Room): void {
    if (this.backstopTimer || this.disposed) return

    // `trackSubscribed` fires for every kind and every participant, but only an
    // audio publication on a peer the roster carries can mean anything here.
    // A camera subscribe used to open an attach-grace entry keyed by the video
    // sid, which `pruneMemory` then dropped on the very next line because it
    // builds its live set from audio publications only — born and deleted in
    // one tick. Server identities are stripped from the publishing roster, so
    // the Moderator's TTS track, which subscribes and unsubscribes around every
    // utterance, was opening a doomed grace entry and driving a full structural
    // scan once per utterance. Neither event can change a verdict; both are
    // dropped here rather than filtered downstream.
    const onSubscribed = (
      _track: unknown,
      publication: { trackSid: string; kind?: string },
      participant?: { identity?: string }
    ) => {
      if (publication.kind !== AUDIO_KIND_ID) return
      if (
        participant?.identity !== undefined &&
        ServerIdentitiesIds.includes(participant.identity)
      ) {
        return
      }
      this.onTrackSubscribed(publication.trackSid)
      this.scanStructural()
    }
    room.on('trackSubscribed', onSubscribed)
    this.unsubscribers.push(() => {
      room.off('trackSubscribed', onSubscribed)
    })
    this.backstopTimer = setInterval(
      () => this.scanStructural(),
      this.backstopIntervalMs
    )
  }

  detach(): void {
    if (this.backstopTimer) {
      clearInterval(this.backstopTimer)
      this.backstopTimer = null
    }
    for (const unsub of this.unsubscribers) unsub()
    this.unsubscribers = []
    this.playheads.clear()
    this.speakers.clear()
    this.attachGraceUntil.clear()
    this.behaviorallyDead.clear()
    this.reportedDead = false
    // The run clocks go with the rest of the per-run state. Moot while
    // `DeadAirController.attach` builds a fresh detector every time, so the
    // stale maps die with the instance either way, but leaving the one piece
    // of run state out of the teardown is how a later reattach-in-place ends
    // up dating a new run to the room before it.
    this.deadSince.clear()
    this.deadRunStartedAt = null
    this.disposed = true
  }

  /** Structural-scan entry point for a slide transition. */
  onSlideChange(): void {
    this.lastSlideChangeAt = this.now()
    this.scanStructural()
  }

  /**
   * Open the attach grace window for a freshly subscribed track, so the scan
   * this subscription triggers doesn't report the not-yet-rendered element as
   * dead air.
   */
  onTrackSubscribed(trackSid: string): void {
    this.attachGraceUntil.set(trackSid, this.now() + this.attachGraceMs)
  }

  /**
   * Walk the publishing roster and emit an episode if any peer is confirmed
   * dead. getStats-free and synchronous — the whole loop reads properties only.
   */
  scanStructural(): void {
    this.repairSuppressedThisScan = false
    const roster = this.env.roster()
    // Before the empty-roster exit, not after it. An empty roster is the case
    // where every remembered track and speaker is stale by definition, so
    // skipping the prune here left the maps at their fullest exactly when
    // nothing in them was still true.
    this.pruneMemory(roster)
    // An empty roster reads two ways and only the connection state separates
    // them. Everyone left, or a full reconnect is in flight: livekit-client's
    // `handleRestarting` disconnects every remote participant before it emits
    // Reconnecting, so mid-repair the room looks empty while the peers are all
    // still in it. Reading that as "nobody is publishing" would clear the
    // standing verdicts and call recovery during the fault.
    //
    // Connected and empty is the other one, and the walk below is right for it:
    // a listener with no publishing peers has nothing they can fail to hear, so
    // a report that still stands is stale. Exiting here instead left the
    // detector latched dead for the rest of the session — the last student in a
    // room everyone else has left keeps a nudge telling them to repair audio
    // with nobody on the other end of it.
    if (roster.length === 0 && this.env.connectionState() !== CONNECTED_STATE) {
      return
    }

    const now = this.now()
    // A peer who left the room is not deaf, and their next run starts when they
    // come back: counting the time they were away is not a duration of anything
    // this client failed to hear. Below the empty-roster guard, not in
    // `pruneMemory`: above the guard an empty roster is a restart artefact, so
    // pruning there would reset every clock on a reconnect flap. This is the
    // same distinction the guard itself draws.
    //
    // Departure only. A peer merely absent from the dead list keeps their
    // clock — see {@link DeadPeer.deadForMs}.
    const present = new Set(roster.map((p) => p.identity))
    for (const identity of [...this.deadSince.keys()]) {
      if (!present.has(identity)) this.deadSince.delete(identity)
    }
    this.armAttachGraceOnJoin(roster, now)
    const deadPeers: DeadPeer[] = []
    for (const peer of roster) {
      const fork = this.classifyPeer(peer, now)
      if (fork) {
        // Whatever the flow checks last read for this peer, if it is still
        // recent. The walk stays synchronous; this is a map lookup.
        const recent = this.recentReceiverStats(peer.identity, now)
        deadPeers.push({
          identity: peer.identity,
          fork,
          wiring: this.env.snapshotPeer(peer.identity),
          receiverStats: recent?.stats,
          receiverStatsAgeMs: recent?.ageMs,
        })
      }
    }
    // A peer who left the room takes their behavioral verdict with them, and a
    // verdict nothing has refreshed eventually expires: a condemned peer sitting
    // quietly is not evidence this client is still deaf, and only their next
    // speaking turn can disprove the verdict on its own terms.
    for (const identity of [...this.behaviorallyDead.keys()]) {
      if (!present.has(identity) || !this.isBehaviorallyDead(identity, now)) {
        this.behaviorallyDead.delete(identity)
      }
    }

    if (deadPeers.length === 0) {
      // A walk the repair guard silenced is not a clean walk. The repair drops
      // every subscription and rebuilds them, so a client still deaf reads
      // spotless for the length of the rebuild, and calling recovery there
      // restarts the run clocks mid-incident and hands the reconnect rung back
      // to a user whose audio never came back.
      if (this.repairSuppressedThisScan) return
      // Dead -> clean edge: tell the UI to stand down before it escalates a
      // stale first episode into a nudge. A clean wiring walk is not enough —
      // a peer dead on a flow check looks perfect to this walk, so recovery
      // waits until the flow checks have cleared them too.
      if (this.reportedDead && this.behaviorallyDead.size === 0) {
        this.reportedDead = false
        // The run is over, so the next one starts from zero. This is the only
        // place the clocks are cleared — the same edge auto-heal re-arms on.
        this.deadSince.clear()
        this.deadRunStartedAt = null
        this.onRecovered?.()
      }
      return
    }

    this.emitEpisode(this.buildEpisode(deadPeers, roster.length))
  }

  /**
   * Re-open the attach-grace window for every live publication the first time
   * a scan runs with the listener joined.
   *
   * Grace is otherwise opened only by `trackSubscribed`, which for anyone
   * already in the room fires during the AV check. The sweeps that keep running
   * through that window spend it — `classifyPublication` deletes an entry the
   * moment it expires — so by the time the user joins there is no grace left
   * for tracks that subscribed minutes ago.
   *
   * The join is the same situation the grace exists for. `livekitRoomReady`
   * flips in `LiveKitRoomWrapper`'s connected handler and the components that
   * attach an `<audio>` element render on the following pass, so a scan landing
   * in between sees the whole roster subscribed and attached to nothing. That is
   * the false positive the join gate exists to prevent, moved to the instant the
   * gate opens.
   *
   * Only a false -> true transition arms it. A detector that first scans with
   * the listener already joined attached mid-session — a reconnect, or a
   * detector built after the meeting UI was up — and there is no pending render
   * to wait for, so a track with nothing attached is dead now, not in two
   * seconds.
   */
  private armAttachGraceOnJoin(roster: DetectorPeer[], now: number): void {
    const joined = this.env.hasListenerJoined()
    const wasJoined = this.joinSeen
    this.joinSeen = joined
    if (!joined || wasJoined !== false) return
    for (const peer of roster) {
      for (const pub of peer.audioPublications) {
        this.attachGraceUntil.set(pub.trackSid, now + this.attachGraceMs)
      }
    }
  }

  /**
   * The single exit for a confirmed episode, from either tier. Marking the
   * detector dead here is what arms the dead -> clean edge above; an emit that
   * bypassed it left the UI's nudge debounce with no way of ever being told the
   * client recovered.
   */
  private emitEpisode(episode: DeadAirEpisode): void {
    // Nothing is reportable before the listener joins — see
    // {@link DeadAirEnvironment.hasListenerJoined}. Dropped rather than held:
    // an episode released at the join would describe a room the user had not
    // entered, against a wiring snapshot taken from a UI that had not mounted.
    // `reportedDead` stays down with it, so the first clean scan after the join
    // is not a dead -> clean edge and does not call recovery for a break
    // nobody was ever told about.
    if (!this.env.hasListenerJoined()) return
    this.stampRunClocks(episode)
    this.reportedDead = true
    this.sink.emit(episode)
  }

  /**
   * Stamp the run clocks onto an episode that is about to be written.
   *
   * Both clocks start on the first episode of a run rather than on the first
   * scan that condemned somebody: a scan the join gate drops is not an episode
   * anybody was told about, and dating a run to one would report a client as
   * deaf through an AV check they were still sitting in.
   */
  private stampRunClocks(episode: DeadAirEpisode): void {
    const now = this.now()
    if (this.deadRunStartedAt === null) this.deadRunStartedAt = now
    episode.deadAirDurationMs = now - this.deadRunStartedAt
    for (const peer of episode.deadPeers) {
      const since = this.deadSince.get(peer.identity) ?? now
      this.deadSince.set(peer.identity, since)
      peer.deadForMs = now - since
    }
  }

  /**
   * The version of the classification below, stamped on every episode.
   *
   * Bump it when the classification logic or the meaning of a fork changes.
   * Do **not** bump it when a threshold is edited in
   * `settings/dead_air_detector`: that is a settings change, and recalibrating
   * without a deploy is what the settings document is for. The runbook carries
   * the same rule.
   *
   * Declared here rather than in a config file, and that placement is the whole
   * anti-rot measure: a constant sitting on top of `classifyPeer` is in the
   * diff of every change that could invalidate it, and one in a config file is
   * not. It is a static on the detector because a module-level const cannot sit
   * inside the class body where the classification lives.
   *
   * Starts at 1 rather than numbering the builds that already shipped. No row
   * before this carries the field, so an absent value means "pre-versioning"
   * and 1 means "the first build that stamps one".
   *
   * Known gap, accepted: a Firestore threshold edit moves the numbers without
   * moving this. If that bites, add a digest of the baked thresholds as a
   * second field rather than overloading this one.
   *
   * History:
   * - 1: first stamped build.
   * - 2: every wiring fork is suppressed while the listener is sequestered
   *   (BL-4998); before this, a sequestered peer's frozen playhead produced
   *   `lost_play`.
   */
  static readonly DETECTOR_VERSION = 2

  /**
   * The peer's first broken audio publication, or null when every one the
   * checks look at is healthy (or expected-silent). Which publications those
   * are is {@link micOrAllPublications}: a break on a screen-share audio track
   * is not dead air.
   *
   * `readOnly` classifies off the same reads without touching grace or playhead
   * memory, for callers that only want to ask. See {@link classifyPublication}.
   */
  private classifyPeer(
    peer: DetectorPeer,
    now: number,
    readOnly = false
  ): DeadAirFork | null {
    for (const pub of micOrAllPublications(peer)) {
      const fork = this.classifyPublication(pub, now, readOnly)
      if (fork) return fork
    }
    return null
  }

  /**
   * The ordered wiring checks for one publication.
   *
   * The live scan is also how the detector maintains its attach-grace and
   * playhead memory, which makes a second caller a hazard: a probe walking these
   * checks would consume grace the real scan had not spent yet and re-stamp
   * playheads it was about to compare against, so a frozen element could look
   * fresh purely because something asked about it. `readOnly` returns the same
   * verdict from the same reads with every write suppressed.
   */
  private classifyPublication(
    pub: DetectorPublication,
    now: number,
    readOnly = false
  ): DeadAirFork | null {
    // A sequestered listener (solo-quiz slides) is silenced on purpose, and
    // every check below reads that silence as a fault. Which check depends on
    // how sequestration silences the peer: `setSubscribed(false)` leaves the
    // publication unsubscribed, but `setEnabled(false)` keeps it subscribed and
    // attached and simply stops the RTP, so the element's playhead freezes and
    // the walk reports `lost_play` — which is BL-4998, a student mid-quiz told
    // to reconnect the moment a finished peer unmuted. Guarding one fork is not
    // enough; the whole walk is expected-silent for as long as it lasts.
    //
    // The playhead memory goes with it. A stamp taken before the quiz would
    // otherwise be compared against the still-frozen element on the first scan
    // after unsequestering, and condemn the peer before the SFU has had the
    // frozenPlayheadMs it is owed to resume.
    if (this.env.isListenerSequestered()) {
      if (!readOnly) this.forgetPlayhead(pub)
      return null
    }

    // A muted publication has nothing to play — expected silence, not dead air.
    if (pub.isMuted) {
      if (!readOnly) this.forgetPlayhead(pub)
      return null
    }

    // SFU shows a publishing peer but we have no local subscription. Only the
    // *silent* case is ours — an already-reported subscription error is not.
    if (!pub.isSubscribed || !pub.audioTrack) {
      if (this.env.hadSubscriptionFailure(pub.trackSid)) return null
      // A repair this client started drops every subscription in the room on
      // purpose, then rebuilds them; the gap between the two is expected
      // silence, the same way sequestration is. Without this the repair
      // reports the whole roster dead a few seconds after the user asked for
      // it, and `not_subscribed` — 78 of the 94 users who reached the durable
      // sink over sixteen days — is mostly the detector reading the wreckage of
      // its own fix rather than a fault anyone heard.
      //
      // Deliberately narrow. Only this fork is suppressed, so a peer that comes
      // back from the repair attached but silent still reports `lost_play` or
      // `sink_divergence` while the repair is in flight.
      if (this.env.isRepairInFlight()) {
        if (!readOnly) this.repairSuppressedThisScan = true
        return null
      }
      return 'not_subscribed'
    }

    const track = pub.audioTrack
    if (track.attachedElements.length === 0) {
      const graceUntil = this.attachGraceUntil.get(pub.trackSid)
      if (graceUntil !== undefined) {
        if (now < graceUntil) return null
        // Grace spent — stop holding the entry for a track that never attached.
        if (!readOnly) this.attachGraceUntil.delete(pub.trackSid)
      }
      return 'not_attached'
    }
    if (!readOnly) this.attachGraceUntil.delete(pub.trackSid)

    const el = track.attachedElements[0]
    const key = track.sid ?? pub.trackSid

    // Paused element, or a non-paused element whose playhead has frozen.
    if (el.paused) {
      if (!readOnly) this.recordPlayhead(key, el.currentTime, now)
      return 'lost_play'
    }
    const prev = this.playheads.get(key)
    const frozen =
      prev !== undefined &&
      prev.currentTime === el.currentTime &&
      now - prev.at >= this.frozenPlayheadMs
    // Reset the freshness stamp whenever the playhead has advanced.
    if (
      !readOnly &&
      (prev === undefined || prev.currentTime !== el.currentTime)
    ) {
      this.recordPlayhead(key, el.currentTime, now)
    }
    if (frozen) return 'lost_play'

    // Playing, but routed to a non-active output device.
    const active = this.env.activeAudioOutput()
    if (
      active !== undefined &&
      normalizeSink(el.sinkId) !== normalizeSink(active) &&
      this.activeSinkAdoptedAnywhere(active)
    ) {
      // Computed on WebKit and then dropped, rather than skipped. The
      // disagreement is still the only signal that a pinned output survived
      // the join-time gates, so it has to keep reaching the wiring snapshot —
      // it just stops being a verdict. Every WebKit report of this fork so far
      // condemned exactly one peer the listener could hear, and the repair
      // matched to it re-issues the `setSinkId` the engine already refused.
      if (this.env.isWebKit) return null
      return 'sink_divergence'
    }

    return null
  }

  /**
   * Whether any attached element in the room is actually playing to the active
   * output device.
   *
   * `room.getActiveDevice('audiooutput')` is not evidence that the device
   * works. LiveKit writes it on the success tail of `switchActiveDevice`, and
   * the app makes that call on the `Connected` event — before any remote track
   * is subscribed, so it routes nothing, resolves over an empty list, reports
   * success, and records an id no element has ever adopted. The per-element
   * `setSinkId` that `RemoteAudioTrack.attach` issues later is allowed to
   * reject silently (LiveKit catches it and logs), and the persisted output
   * choice is read straight from localStorage with no check that the device is
   * still plugged in. So the ordinary "picked a headset once, joined without
   * it" case leaves every element on the system default and the room's
   * bookkeeping on a dead id, permanently.
   *
   * Condemning on that disagreement alone marks the entire roster dead on a
   * client that can hear everyone, and the damage does not stop at the report:
   * the `resink` repair re-issues the same rejecting call, spends the peer's
   * cheap fix, and the ladder walks the user through a reconnect and then a
   * reload that re-reads the same dead device id.
   *
   * Requiring one adopter separates the two cases. A device that works has at
   * least one element on it, so a peer that failed to re-route while its
   * neighbours succeeded is still condemned. A device that works nowhere is the
   * room's bookkeeping, not this peer's wiring.
   *
   * The cost is a genuine routing failure against a working device in a room
   * with a single audible peer, where there is no neighbour to corroborate.
   * That case is indistinguishable from the false one on the evidence
   * available, and staying silent is the cheaper mistake: the element is on the
   * system default, which is where the user most likely is.
   */
  private activeSinkAdoptedAnywhere(active: string): boolean {
    const target = normalizeSink(active)
    // The default device is the resting state, not something `setSinkId` had
    // to succeed at, and it always exists. An element routed away from it is a
    // real divergence with nothing to corroborate.
    if (target === 'default') return true
    for (const peer of this.env.roster()) {
      for (const pub of peer.audioPublications) {
        for (const el of pub.audioTrack?.attachedElements ?? []) {
          if (normalizeSink(el.sinkId) === target) return true
        }
      }
    }
    return false
  }

  /**
   * Whether a flow verdict still stands against this peer at `now`.
   *
   * Every read of {@link behaviorallyDead} goes through here, because the
   * expiry has to be honoured wherever the verdict is consulted, not only
   * where it is pruned. `evaluateHeal` runs between scans by design — that is
   * the point of a post-repair probe — so a membership test alone would let a
   * verdict that had already aged out report the repair as failed.
   */
  private isBehaviorallyDead(identity: string, now: number): boolean {
    const verdict = this.behaviorallyDead.get(identity)
    if (!verdict) return false
    return now - verdict.at <= BEHAVIORAL_VERDICT_TTL_MS
  }

  /**
   * Drop what the roster no longer carries: playhead and attach-grace entries
   * for departed tracks, speaker turn state for departed peers.
   *
   * Neither map is cleaned by the walk that fills it. `classifyPublication`
   * only releases grace for a publication it is still walking, and a muted one
   * returns before it gets there; `forgetPlayhead` runs on mute alone. So a
   * peer who leaves, or a sid that churns across a resubscribe, leaves an entry
   * with nothing left to clear it — the maps only ever grow over a long call.
   *
   * Stale playhead memory is also wrong, not merely large: the frozen check
   * compares against whatever `currentTime` was recorded last and how long ago,
   * so a resubscribed track landing back on its old value reads as a playhead
   * that has not moved for the whole gap, and the peer is reported `lost_play`
   * while playing normally.
   *
   * Those two maps are keyed by track sid rather than by peer, so the live set
   * names every sid either could have used: `attachGraceUntil` keys on the
   * publication's sid, `playheads` on the decoded track's sid where there is
   * one. Every publication counts here, not just the mic — grace is opened by
   * `trackSubscribed` for any audio track on a rostered peer, whoever the
   * checks later judge.
   *
   * `speakers` is keyed by identity and swept separately. Its own sweep, at the
   * end of `onActiveSpeakers`, needs a speaker event to run, and the event where
   * the room falls silent is too early to evict anyone — `lastSeenAt` was just
   * written. Whoever spoke last keeps their turn state until the next event,
   * which may never come. That is bounded by the participant count rather than
   * unbounded, so the reason to drop it here is not size: an entry that outlives
   * its peer carries a baseline captured against a receiver that is gone, and
   * dropping it is what makes the peer re-baseline if they ever come back.
   */
  private pruneMemory(roster: DetectorPeer[]): void {
    const liveSids = new Set<string>()
    const liveIdentities = new Set<string>()
    for (const peer of roster) {
      liveIdentities.add(peer.identity)
      for (const pub of peer.audioPublications) {
        liveSids.add(pub.trackSid)
        if (pub.audioTrack?.sid) liveSids.add(pub.audioTrack.sid)
      }
    }
    for (const sid of [...this.playheads.keys()]) {
      if (!liveSids.has(sid)) this.playheads.delete(sid)
    }
    for (const sid of [...this.attachGraceUntil.keys()]) {
      if (!liveSids.has(sid)) this.attachGraceUntil.delete(sid)
    }
    for (const identity of [...this.speakers.keys()]) {
      if (!liveIdentities.has(identity)) this.speakers.delete(identity)
    }
    for (const identity of [...this.lastReceiverStats.keys()]) {
      if (!liveIdentities.has(identity)) this.lastReceiverStats.delete(identity)
    }
  }

  private recordPlayhead(key: string, currentTime: number, at: number): void {
    this.playheads.set(key, { currentTime, at })
  }

  private forgetPlayhead(pub: DetectorPublication): void {
    const key = pub.audioTrack?.sid ?? pub.trackSid
    this.playheads.delete(key)
  }

  /**
   * Flow checks — single-peer, speaker-gated. `activeSpeakersChanged` is the
   * SFU's "this peer is producing audio" signal; only then is zero bytes/energy
   * distinguishable from a peer simply being silent. The gate accumulates
   * continuous active-speaker time per remote speaker and, once one peer
   * sustains the threshold, does exactly ONE getStats for THAT peer — never a
   * per-event roster sweep. Async, fire-and-forget from the caller.
   */
  async onActiveSpeakers(
    speakers: { identity: string }[],
    now: number = this.now()
  ): Promise<void> {
    if (this.disposed) return
    const listener = this.env.listenerId()
    // Server identities are out of scope for the same reason they are stripped
    // from the publishing roster: the detector reports on whether a listener can
    // hear their *peers*. Bots reach this handler — the Moderator's track
    // carries both TTS and the solo bot, and `Breakout Learning` publishes the
    // case video's audio, so both appear in `activeSpeakersChanged` — and
    // nothing downstream would stop one being condemned. An episode naming a bot
    // would then report `deadPeers` and `peersPublishing` over different
    // populations, the same defect the `peersSubscribed` filter closed.
    const activeIds = new Set(
      speakers
        .map((s) => s.identity)
        .filter((id) => id !== listener && !ServerIdentitiesIds.includes(id))
    )

    // Set by the sustained gate below and acted on after the loop, so the scan
    // never runs while this loop still holds a reference into `this.speakers`.
    // `scanStructural` prunes that map of anyone off the publishing roster, and
    // mid-loop it can delete the entry the loop is still writing to. The entry
    // is dropped by the same prune either way, so this buys ordering clarity
    // rather than a behaviour change; it also collapses two speakers crossing
    // the gate in one event into a single sweep.
    let sweepWiring = false

    for (const id of activeIds) {
      if (this.disposed) return
      const existing = this.speakers.get(id)
      if (!existing) {
        // First sighting starts a turn but spends no getStats: most sightings
        // are sub-second VAD flickers that never reach the sustained gate.
        this.speakers.set(id, {
          activeMs: 0,
          lastSeenAt: now,
          turnStartedAt: now,
          baseline: null,
          baselineAttempted: false,
          evaluated: false,
          structurallyScanned: false,
        })
        continue
      }

      const gap = now - existing.lastSeenAt
      existing.lastSeenAt = now

      // Only continuous speech counts. A larger gap ends the turn, and
      // everything the old turn carried is now stale: the accumulated time, the
      // baseline the deltas would be measured against, and the fact that we
      // already judged this speaker. Resetting all three is what lets a speaker
      // be re-checked on their next turn — without it, someone who merely
      // pauses between sentences is never evicted (eviction needs absence, and
      // they are still in activeIds), so they are judged exactly once per
      // meeting, against a baseline captured before the pause.
      if (gap > this.speakerFlickerGapMs) {
        existing.activeMs = 0
        existing.turnStartedAt = now
        existing.baseline = null
        existing.baselineAttempted = false
        existing.evaluated = false
        existing.structurallyScanned = false
        continue
      }

      // Someone has been talking continuously for the sustained window, so
      // sweep the wiring as well as the flow. The wiring faults are invisible
      // to everything below this line: a paused element still receives RTP, so
      // `lost_play` — the fault production reports most — reads healthy to
      // getStats, and a peer nobody subscribed to has no receiver to read at
      // all. Without this the only sweep that can see them is the 30s backstop,
      // and the worst case is a listener sitting deaf through half a minute of
      // someone speaking with no slide change to trigger a scan.
      //
      // Ahead of the baseline branch below on purpose: that branch bails out
      // for the whole turn when there are no stats to read, which is exactly
      // the state `not_subscribed` produces.
      if (
        !existing.structurallyScanned &&
        now - existing.turnStartedAt >= this.sustainedSpeakerMs
      ) {
        existing.structurallyScanned = true
        sweepWiring = true
      }

      if (!existing.baselineAttempted) {
        // Second consecutive sighting: a real turn, so it earns the one
        // getReceiverStats the flow checks are allowed. activeMs restarts
        // here so the sustained window and the stats delta span the same time.
        existing.baselineAttempted = true
        existing.baseline = (await this.readReceiverStats(id)) ?? null
        existing.activeMs = 0
        continue
      }

      // Attempted but no baseline covers two states, and neither one should
      // accumulate. Either the capture above is still in flight — this handler
      // is re-entered when a second speaker event lands inside its await, and
      // the increment would be wiped by the `activeMs = 0` that runs when the
      // first call resolves — or the capture already returned nothing, in which
      // case the turn can never be evaluated and the total is never read.
      // `lastSeenAt` is stamped above, so skipping here does not evict anyone.
      if (existing.baselineAttempted && !existing.baseline) continue

      existing.activeMs += gap
      if (
        !existing.evaluated &&
        existing.baseline &&
        existing.activeMs >= this.sustainedSpeakerMs
      ) {
        existing.evaluated = true
        await this.evaluateSpeaker(id, existing.baseline)
      }
    }

    // Drop speakers unseen past the reset window so their next turn re-baselines.
    for (const [id, state] of this.speakers) {
      if (
        !activeIds.has(id) &&
        now - state.lastSeenAt > SPEAKER_WINDOW_RESET_MS
      ) {
        this.speakers.delete(id)
      }
    }

    // Last, with every accumulator for this event already written.
    if (sweepWiring && !this.disposed) this.scanStructural()
  }

  /**
   * The only way the flow checks read stats. `getReceiverStats` bottoms out in
   * `receiver.getStats()`, a browser call that rejects on a closed peer
   * connection, during teardown, and on a receiver mid-renegotiation — none of
   * which the detector can prevent. Both callers run under the `void`
   * fire-and-forget the controller uses, so an escaping rejection is unhandled,
   * and the one in `onActiveSpeakers` would also abandon every remaining speaker
   * in that event's list.
   *
   * A throw carries exactly the information a missing field does: nothing. It
   * gets the same answer — undefined, which every caller already reads as
   * "cannot evaluate" rather than as silence.
   */
  private async readReceiverStats(
    identity: string
  ): Promise<AudioReceiverStats | undefined> {
    try {
      const stats = await this.env.getReceiverStats(identity)
      // Cached here rather than at the call sites so both flow reads feed it —
      // the turn-onset baseline as well as the verdict read. The baseline is
      // the earlier of the two and often the only one a short turn produces,
      // and a structural sweep moments later is exactly when it is worth
      // having.
      if (stats) this.lastReceiverStats.set(identity, { stats, at: this.now() })
      return stats
    } catch {
      return undefined
    }
  }

  /**
   * The last inbound-RTP sample for a peer, with its age, if it is recent
   * enough to describe the room the caller is looking at.
   *
   * The bound is one backstop interval, read from the configured value rather
   * than the default: the structural walk is the only caller, so a sample older
   * than one of its own cycles describes a room that has had a full sweep to
   * change, and a reader who cannot tell that from a live read would date the
   * silence wrong. Pinned to the default, the bound stopped meaning that
   * wherever ops moved the sweep, which the audio suite does on every profile.
   * Past the bound the episode carries nothing, which is honest, rather than a
   * number that has stopped being true.
   */
  private recentReceiverStats(
    identity: string,
    now: number
  ): { stats: AudioReceiverStats; ageMs: number } | undefined {
    const entry = this.lastReceiverStats.get(identity)
    if (!entry) return undefined
    const ageMs = now - entry.at
    if (ageMs > this.backstopIntervalMs) return undefined
    return { stats: entry.stats, ageMs }
  }

  /**
   * One getStats for the sustained speaker, compared against its turn-onset
   * baseline. Fires `delivery_stalled` (bytes frozen) or `silent_decode` (bytes
   * flow but the decoder emits no energy), with the mandatory exclusions — and
   * it degrades rather than false-firing when a browser doesn't expose a field.
   */
  private async evaluateSpeaker(
    identity: string,
    baseline: AudioReceiverStats
  ): Promise<void> {
    const current = await this.readReceiverStats(identity)
    // detach() may have run while getStats was in flight; emitting now would
    // push an episode through a torn-down controller.
    if (this.disposed) return
    if (!current) return
    // Exclusions: an isolated listener or a muted publisher both legitimately
    // produce silence — not dead air.
    if (this.env.isListenerSequestered()) return
    if (this.env.isSpeakerAudioMuted(identity)) return
    // And a listener still on the AV check is not deaf to a room they have not
    // entered. This is an exclusion rather than a drop at the emit gate below,
    // because the verdict this method records outlives the episode: a standing
    // `behaviorallyDead` entry is consulted by `evaluateHeal` and blocks
    // `onRecovered`, so a pre-join verdict would still be condemning the peer
    // for up to BEHAVIORAL_VERDICT_TTL_MS after the user walked in — long
    // enough to send a heal that was going to work to the reconnect dialog.
    if (!this.env.hasListenerJoined()) return

    const deltaBytes = current.bytesReceived - baseline.bytesReceived
    let fork: DeadAirFork | null = null

    // A counter that went backwards is not a stall. bytesReceived belongs to
    // one receiver, and a mid-turn resubscribe — a reconnect, an ICE restart, a
    // track republish — hands back a new receiver counting from zero, so the
    // delta reads as a large negative number. Condemning on it reports dead air
    // for a connection that was being rebuilt.
    //
    // The turn is abandoned rather than re-baselined against the new receiver.
    // A fresh subscription legitimately delivers nothing for a moment; the
    // wiring walk covers that with attachGraceUntil, and the flow checks have
    // no such window, so an immediate re-check would judge the peer during the
    // one interval that is expected to be empty. `evaluated` stays latched, and
    // the speaker is measured again on their next turn against a settled
    // receiver.
    if (deltaBytes < 0) return // resubscribed mid-turn: nothing to compare.

    if (deltaBytes <= BYTES_STALLED_EPSILON) {
      // No bytes arrived across a sustained speaking turn — delivery is dead.
      fork = 'delivery_stalled'
    } else {
      // Bytes are flowing again, which disproves a delivery_stalled verdict on
      // its own terms — no energy field required. Without this the verdict is a
      // one-way latch on any browser that doesn't expose totalAudioEnergy: the
      // add path below needs no energy, but the only other exit does, so one
      // stall would suppress onRecovered for the rest of the session on exactly
      // those browsers.
      if (this.behaviorallyDead.get(identity)?.fork === 'delivery_stalled') {
        this.behaviorallyDead.delete(identity)
      }
      // Bytes flow — is the decoder producing sound? Degrade if the field is
      // unavailable rather than reading absence as silence.
      if (
        current.totalAudioEnergy === undefined ||
        baseline.totalAudioEnergy === undefined
      ) {
        return // energy_unavailable: cannot evaluate decode; do not assert.
      }
      const deltaEnergy = current.totalAudioEnergy - baseline.totalAudioEnergy
      // Same counter-reset shape as the byte guard above, and inconclusive for
      // the same reason. This is the unlikelier half of the pair: reaching it
      // needs bytes to have risen across the very reset that dropped energy,
      // and the two counters climb together on a real receiver. The guard is
      // here because flat-looking energy is what condemns a peer as
      // silent_decode, and a counter that went backwards has not been measured.
      if (deltaEnergy < 0) return
      if (deltaEnergy > this.energyEpsilon) {
        // The decoder produced sound: this peer is audible again. The only
        // positive proof of health a flow check can offer, so it is the only
        // place a flow verdict is lifted. Every other early return above means
        // "cannot tell", which must not read as recovery.
        this.behaviorallyDead.delete(identity)
        return
      }

      // Flat energy is a silent-decode candidate — unless packet loss masked by
      // concealment explains it (network, not dead air). Can't run that
      // exclusion without concealment fields → downgrade, don't assert.
      if (
        current.concealedSamples === undefined ||
        baseline.concealedSamples === undefined
      ) {
        return // inconclusive: masked-loss exclusion unavailable.
      }
      if (this.isMaskedPacketLoss(current, baseline)) return
      fork = 'silent_decode'
    }

    this.behaviorallyDead.set(identity, { fork, at: this.now() })
    // Flow peers carry the wiring snapshot plus the inbound-rtp stats. They
    // stay separate fields: merged, a wiring key added later would overwrite a
    // receiver counter in the very report used to read the failure.
    this.emitEpisode(
      this.buildEpisode(
        [
          {
            identity,
            fork,
            wiring: this.env.snapshotPeer(identity),
            receiverStats: current,
            // Read moments ago, on this peer, to reach this verdict.
            receiverStatsAgeMs: 0,
          },
        ],
        this.env.roster().length,
        identity
      )
    )
  }

  /**
   * A large fraction of concealed samples over the window means the flat energy
   * is packet-loss concealment (silence fill), not a dead decode.
   *
   * Both counters come off the same receiver and concealed samples are a subset
   * of the samples received, so the fraction is measured rather than inferred
   * from an assumed sample rate.
   */
  private isMaskedPacketLoss(
    current: AudioReceiverStats,
    baseline: AudioReceiverStats
  ): boolean {
    const deltaConcealed =
      (current.concealedSamples ?? 0) - (baseline.concealedSamples ?? 0)
    if (deltaConcealed <= 0) return false
    const deltaSamples =
      (current.totalSamplesReceived ?? 0) - (baseline.totalSamplesReceived ?? 0)
    if (deltaSamples <= 0) return false
    return deltaConcealed / deltaSamples > this.concealFraction
  }

  private buildEpisode(
    deadPeers: DeadPeer[],
    peersPublishing: number,
    triggeringSpeaker: string | null = null
  ): DeadAirEpisode {
    // One read, carried on the episode, rather than a second read at emit time.
    // `buildPeerWiringSnapshot` reads the same PeerConnection for its own
    // report, and the two run far enough apart that a transport flapping across
    // the gap would have an episode whose gate and whose snapshot disagree —
    // the exact contradiction that makes a report unreadable.
    const transport = this.env.subscriberTransport()
    return {
      listenerId: this.env.listenerId(),
      deadPeers,
      peersSubscribed: this.env.peersSubscribed(),
      peersPublishing,
      triggeringSpeaker,
      pageHidden: this.env.pageHidden(),
      connectionState: this.env.connectionState(),
      subscriberIceState: transport.ice,
      subscriberPcState: transport.pc,
      detectorVersion: DeadAirDetector.DETECTOR_VERSION,
      livekitConnectionQuality: this.env.connectionQuality(),
      roomParticipantCount: this.env.participantCount(),
      secondsSinceSlideChange:
        this.lastSlideChangeAt === null
          ? undefined
          : (this.now() - this.lastSlideChangeAt) / 1000,
    }
  }

  /**
   * Non-emitting recovery probe. After a heal attempt, re-check the
   * previously-dead peers and report which are healthy now vs still broken. A
   * peer that has left the roster counts as recovered (it can no longer be a
   * dead-air source). Never emits an episode, and never mutates.
   *
   * A peer is still dead if the wiring walk finds a fault *or* a flow verdict
   * still stands against them. The wiring walk alone was the bug: a
   * flow-condemned peer is subscribed, attached and playing as far as it can
   * see, so the probe reported "recovered" for precisely the faults that
   * prompted the repair. Consulting the verdicts is deliberately conservative —
   * a flow heal cannot be confirmed until the peer next speaks — which is the
   * safe direction for a signal that is now read as telemetry.
   *
   * `pending` is the third answer the two-bucket version could not give. A heal
   * that resubscribes a peer opens a fresh attach-grace window through this
   * detector's own `trackSubscribed` listener, and a probe running inside that
   * window sees a not-yet-attached publication the wiring checks deliberately
   * refuse to condemn — so it returned `recovered` on the strength of a grace
   * period the heal itself had just opened. Grace is right to suppress that: an
   * element that has not rendered yet is no evidence either way. The bug was
   * forcing "no evidence" into a binary, so it is reported as its own state and
   * the caller can ask again once the window closes.
   *
   * A standing flow verdict stays in `stillDead` rather than moving here. It is
   * a condemnation on measured evidence that nothing has disproved, which is not
   * the same as having no evidence, and `clearBehavioralVerdicts` already drops
   * verdicts for a repair that resubscribed the peer. `stillDead` wins over
   * `pending` when a peer qualifies for both.
   */
  evaluateHeal(identities: string[]): {
    recovered: string[]
    stillDead: string[]
    pending: string[]
  } {
    const now = this.now()
    const roster = this.env.roster()
    const recovered: string[] = []
    const stillDead: string[] = []
    const pending: string[] = []
    for (const id of identities) {
      const peer = roster.find((p) => p.identity === id)
      if (!peer) {
        recovered.push(id)
        continue
      }
      const wiringDead = this.classifyPeer(peer, now, true) !== null
      if (wiringDead || this.isBehaviorallyDead(id, now)) {
        stillDead.push(id)
      } else if (this.isWithinAttachGrace(peer, now)) {
        pending.push(id)
      } else {
        recovered.push(id)
      }
    }
    return { recovered, stillDead, pending }
  }

  /**
   * Whether any publication the wiring checks judge this peer on is sitting in
   * an unexpired attach-grace window with nothing attached yet.
   *
   * Scoped to {@link micOrAllPublications} rather than every audio publication,
   * for the same reason the checks themselves are: a screen-share audio track
   * inside its grace window must not report a peer as pending when the mic it
   * is judged on is attached and fine.
   *
   * Reads only. `classifyPublication` is where grace is spent, and the probe
   * runs in `readOnly` precisely so it cannot spend any.
   */
  private isWithinAttachGrace(peer: DetectorPeer, now: number): boolean {
    for (const pub of micOrAllPublications(peer)) {
      if (pub.audioTrack && pub.audioTrack.attachedElements.length > 0) continue
      const graceUntil = this.attachGraceUntil.get(pub.trackSid)
      if (graceUntil !== undefined && now < graceUntil) return true
    }
    return false
  }

  /**
   * Drop any standing flow verdicts against these peers. Called once a repair
   * has re-subscribed them: the verdicts describe a subscription that no longer
   * exists, and leaving them latched would keep the client marked deaf until the
   * TTL. A fault that survived the repair re-condemns the peer on their next
   * speaking turn, which is what escalates them.
   */
  clearBehavioralVerdicts(identities: string[]): void {
    for (const identity of identities) this.behaviorallyDead.delete(identity)
  }
}

/**
 * Adapter: map a real LiveKit `Room` (plus the controller-only providers the
 * room can't answer) onto {@link DeadAirEnvironment}. Reads a fresh roster
 * snapshot each call so the detector always classifies current state.
 */
export function createLivekitDeadAirEnvironment(
  room: Room,
  providers: {
    listenerId: () => string
    pageHidden: () => boolean
    hadSubscriptionFailure: (trackSid: string) => boolean
    isListenerSequestered: () => boolean
    isRepairInFlight: () => boolean
    hasListenerJoined: () => boolean
    /** Cached output-device picture; read synchronously at snapshot time. */
    outputDevices: () => OutputDeviceSnapshot
  }
): DeadAirEnvironment {
  return {
    listenerId: () => providers.listenerId(),
    pageHidden: () => providers.pageHidden(),
    hadSubscriptionFailure: (sid) => providers.hadSubscriptionFailure(sid),
    isListenerSequestered: () => providers.isListenerSequestered(),
    isRepairInFlight: () => providers.isRepairInFlight(),
    hasListenerJoined: () => providers.hasListenerJoined(),
    isWebKit: isWebKit(),
    activeAudioOutput: () => room.getActiveDevice('audiooutput'),
    connectionState: () => String(room.state),
    // Guarded like every other read that goes through a livekit shape we do
    // not own: an SDK change costs the column, not the episode.
    connectionQuality: () =>
      guardedRead(() => {
        const quality = room.localParticipant?.connectionQuality
        return quality === undefined ? undefined : String(quality)
      }),
    participantCount: () =>
      guardedRead(() => {
        let humans = 0
        room.remoteParticipants.forEach((p) => {
          if (ServerIdentitiesIds.includes(p.identity)) return
          humans++
        })
        // The listener is in the room too, and a count that left them out
        // would report a pair as a room of one. Filtered on the same rule the
        // remote walk uses, so the exclusion this count claims is true of the
        // whole count rather than of half of it. The local participant is the
        // browser's own user today, so this cannot subtract; it is here so the
        // next reader does not have to trace the call graph to find out whether
        // the asymmetry was deliberate.
        const localIsHuman = !ServerIdentitiesIds.includes(
          room.localParticipant?.identity ?? ''
        )
        return humans + (localIsHuman ? 1 : 0)
      }),
    subscriberTransport: () => readSubscriberTransport(room),
    roster: () => readPublishingRoster(room),
    peersSubscribed: () => {
      let count = 0
      room.remoteParticipants.forEach((p) => {
        // Same exclusion the publishing roster applies. Without it an episode
        // reports two counts drawn from different populations, and the bot
        // contribution flickers with the Moderator's TTS track, which
        // subscribes and unsubscribes around every utterance.
        if (ServerIdentitiesIds.includes(p.identity)) return
        for (const pub of p.audioTrackPublications.values()) {
          if (pub.isSubscribed) {
            count++
            break
          }
        }
      })
      return count
    },
    isSpeakerAudioMuted: (identity) => {
      const p = room.remoteParticipants.get(identity)
      if (!p) return false
      // The mic is what `activeSpeakersChanged` reports on, so it alone decides
      // whether this speaker is expected-silent. Reading "any unmuted audio
      // publication" would call a muted speaker unmuted on the strength of a
      // screen-share track nobody is talking over.
      const mic = findMicPublication(p)
      if (mic) return mic.isMuted
      for (const pub of p.audioTrackPublications.values()) {
        if (!pub.isMuted) return false
      }
      return p.audioTrackPublications.size > 0
    },
    getReceiverStats: async (identity) => {
      const p = room.remoteParticipants.get(identity)
      if (!p) return undefined
      // Prefer the mic explicitly. The flow checks measure the track that just
      // triggered `activeSpeakersChanged`; taking whichever publication the map
      // happens to yield first would compare bytes and energy against a
      // screen-share track instead, and then either false-fire
      // `delivery_stalled` or miss a real stall on the mic.
      const mic = findMicPublication(p)
      const pubs = mic ? [mic] : Array.from(p.audioTrackPublications.values())
      for (const pub of pubs) {
        // `receiver` only exists on remote tracks; check it exists rather than
        // an instanceof (type exports don't survive bundling — see BroadcastMonitor).
        // The cast is what narrows a local track out, so it also stands in for
        // the instanceof the stats reader would otherwise want.
        const track = pub.audioTrack as StatsReadableTrack | undefined
        if (track?.receiver) {
          return unpackAudioReceiverStats(track)
        }
      }
      return undefined
    },
    snapshotPeer: (identity) =>
      buildPeerWiringSnapshot(room, identity, providers.outputDevices()),
  }
}

/** Output-device picture the controller caches (enumerateDevices is async). */
export interface OutputDeviceSnapshot {
  audioOutputs: { deviceId: string; label: string }[]
  activeSinkId?: string
  lastDeviceChangeAt: number | null
}

/** Run a read that may throw on a missing/renamed field; undefined on throw. */
function guardedRead<T>(fn: () => T): T | undefined {
  try {
    return fn()
  } catch {
    return undefined
  }
}

/**
 * Reach the subscriber RTCPeerConnection through livekit internals. This is the
 * one deliberately fragile read; a future SDK layout change costs these fields,
 * not a crash, because every caller guards it.
 */
function findSubscriberPeerConnection(
  room: Room
): RTCPeerConnection | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const anyRoom = room as any
  return (
    guardedRead(() => anyRoom.engine.pcManager.subscriber.pc) ??
    guardedRead(() => anyRoom.engine.subscriber.pc) ??
    guardedRead(() => anyRoom.engine.pcManager.subscriberPC)
  )
}

/**
 * The subscriber transport's state, by two independent routes.
 *
 * The first is the engine's own PeerConnection. The second exists because the
 * first is documented above as the one deliberately fragile read, and that was
 * an acceptable bargain only while nothing was gated on it — a telemetry field
 * that degrades to `undefined` costs a column in a report, whereas a *gate*
 * that degrades to `undefined` silently restores the false roster-wide reports
 * it was added to stop. So when the internals move, fall back to the receiver
 * we already hold for the track being classified and read its ICE transport
 * directly. Every subscription in the room is bundled onto that one transport,
 * so any receiver answers for all of them and the first one found will do.
 *
 * `RTCDtlsTransport.iceTransport` is not universal, which is why it is the
 * fallback and not the primary: on a browser without it both routes can miss,
 * and both callers treat a missing answer as a healthy transport.
 */
function readSubscriberTransport(room: Room): { ice?: string; pc?: string } {
  const pc = findSubscriberPeerConnection(room)
  if (pc) {
    const ice = guardedRead(() => pc.iceConnectionState)
    const conn = guardedRead(() => pc.connectionState)
    if (ice !== undefined || conn !== undefined) return { ice, pc: conn }
  }
  // Guarded around the whole walk, not just the property read at the bottom.
  // Every read in this file is guarded because the detector is a diagnostic
  // path and must never be the reason a meeting fails to set up, and iterating
  // `remoteParticipants` is itself a read: a room whose shape has moved throws
  // on `.values()` before any inner guard is reached, and this runs inside
  // `buildEpisode`, which runs inside `scanStructural`, which the backstop
  // timer and every slide change call.
  const ice = guardedRead(() => {
    for (const participant of room.remoteParticipants.values()) {
      for (const pub of participant.audioTrackPublications.values()) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const receiver = (pub.audioTrack as any)?.receiver
        const state = receiver?.transport?.iceTransport?.state
        if (typeof state === 'string') return state
      }
    }
    return undefined
  })
  return ice === undefined ? {} : { ice }
}

function elementSnapshot(el: HTMLMediaElement): Record<string, unknown> {
  const withExtras = el as HTMLMediaElement & {
    sinkId?: string
    srcObject?: MediaStream | null
  }
  return {
    paused: guardedRead(() => el.paused),
    ended: guardedRead(() => el.ended),
    readyState: guardedRead(() => el.readyState),
    networkState: guardedRead(() => el.networkState),
    muted: guardedRead(() => el.muted),
    volume: guardedRead(() => el.volume),
    currentTime: guardedRead(() => el.currentTime),
    errorCode: guardedRead(() => el.error?.code),
    sinkId: guardedRead(() => withExtras.sinkId),
    bufferedRanges: guardedRead(() => el.buffered.length),
    playedRanges: guardedRead(() => el.played.length),
    // Whether the element is wired to a live audio track vs a stale/absent one.
    srcObjectLive: guardedRead(() => {
      const so = withExtras.srcObject
      if (!so) return false
      return so.getAudioTracks().some((t) => t.readyState === 'live')
    }),
  }
}

/**
 * Assemble the diagnostic wiring snapshot for one dead peer. Every read is
 * guarded; the whole thing is emit-time telemetry and is never gated on.
 */
function buildPeerWiringSnapshot(
  room: Room,
  identity: string,
  devices: OutputDeviceSnapshot
): Record<string, unknown> {
  const activeSink = devices.activeSinkId
  const sinkValid =
    activeSink === undefined ||
    activeSink === '' ||
    activeSink === 'default' ||
    devices.audioOutputs.some((d) => d.deviceId === activeSink)

  const snapshot: Record<string, unknown> = {
    audioOutputs: devices.audioOutputs,
    activeSinkId: activeSink ?? null,
    sinkValid,
    lastDeviceChangeAt: devices.lastDeviceChangeAt,
    pcConnectionState: guardedRead(
      () => findSubscriberPeerConnection(room)?.connectionState
    ),
    pcIceConnectionState: guardedRead(
      () => findSubscriberPeerConnection(room)?.iceConnectionState
    ),
  }

  const p = room.remoteParticipants.get(identity)
  if (!p) return snapshot

  // The mic, on the same terms the checks use. Every read that decides whether
  // a peer is audible now names the microphone — `micOrAllPublications` for the
  // wiring walk, `findMicPublication` for the flow checks — and this snapshot is
  // where someone reads that verdict back against the evidence. Describing a
  // screen-share track under a verdict reached on the mic is worse here than in
  // either check alone, because the mismatch is invisible in the report.
  const mic = findMicPublication(p)
  const pubs = mic ? [mic] : Array.from(p.audioTrackPublications.values())

  for (const pub of pubs) {
    const track = pub.audioTrack
    if (!track) continue
    snapshot.elements = guardedRead(() =>
      track.attachedElements.map((el) => elementSnapshot(el))
    )
    const mst = guardedRead(() => track.mediaStreamTrack)
    if (mst) {
      snapshot.trackReadyState = guardedRead(() => mst.readyState)
      snapshot.trackMuted = guardedRead(() => mst.muted)
      snapshot.trackEnabled = guardedRead(() => mst.enabled)
      snapshot.trackId = guardedRead(() => mst.id)
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const receiver = (track as any).receiver as RTCRtpReceiver | undefined
    if (receiver) {
      snapshot.dtlsState = guardedRead(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        () => (receiver as any).transport?.state
      )
      const pc = findSubscriberPeerConnection(room)
      const transceiver = guardedRead(() =>
        pc?.getTransceivers().find((t) => t.receiver === receiver)
      )
      if (transceiver) {
        snapshot.transceiverDirection = guardedRead(
          () => transceiver.currentDirection
        )
        snapshot.transceiverMid = guardedRead(() => transceiver.mid)
      }
    }
    break
  }
  return snapshot
}

/**
 * Snapshot the publishing roster from a real room into detector shapes.
 *
 * Server participants are excluded. The Moderator's TTS track subscribes and
 * unsubscribes around every utterance, so any scan landing in that window would
 * classify a bot as dead. They also inflate `peersPublishing`, and a
 * whole-client stall is read as every publishing peer being dead at once
 * (`deadPeers.length === peersPublishing`); a room of one human plus the
 * Moderator would otherwise never look fully dead.
 */
export function readPublishingRoster(room: Room): DetectorPeer[] {
  const peers: DetectorPeer[] = []
  room.remoteParticipants.forEach((p: RemoteParticipant) => {
    if (ServerIdentitiesIds.includes(p.identity)) return
    const pubs = Array.from(p.audioTrackPublications.values())
    if (pubs.length === 0) return
    peers.push({
      identity: p.identity,
      audioPublications: pubs.map(mapPublication),
    })
  })
  return peers
}

function mapPublication(pub: RemoteTrackPublication): DetectorPublication {
  const track = pub.audioTrack
  return {
    trackSid: pub.trackSid,
    isSubscribed: pub.isSubscribed,
    isMuted: pub.isMuted,
    source: pub.source,
    audioTrack: track
      ? {
          sid: track.sid,
          attachedElements: track.attachedElements.map((el) => ({
            paused: el.paused,
            currentTime: el.currentTime,
            sinkId: (el as HTMLMediaElement & { sinkId?: string }).sinkId,
          })),
        }
      : undefined,
  }
}
